import React, { useEffect, useRef, ReactNode, forwardRef } from "react";
import useState from 'react-usestateref';
import classnames from 'classnames';
import './ByzzerPPGGroupPicker.scss';
import { ByzzerChangeEventHandler, ByzzerSelectOption, ByzzerWindowedSelect, WithValue } from "@byzzer/ui-components";
import { useTenantApi } from "@/hooks/useTenantApi";
import { SingleValue } from "react-select";
import { RunConfigMarket } from "@/types/ReportRun";
import { createPpgGroupNameOption } from "@/views/simulators/utils";
import { PpgGroupNames } from "@/types/ApiTypes";


export type ByzzerPPGGroupPickerProps = {
    name?: string;
    label?: ReactNode;
    onChange?: ByzzerChangeEventHandler<string[]>;
    value?: string[];
    disabledOptions?: string[];
    placeholder?: string;
    className?: string;
    disabled?: boolean;
    allowClear?: boolean;
    showDescription?: boolean;
    onlyRenderIf?: boolean;

    ppgId: number;

    // filters: DefaultPPGInfoFilters,

    categories: string[]; // needs conversion to keys
    brands?: string[];
    parentCompanies?: string[];
    manufacturers?: string[];
    markets?: RunConfigMarket[]; // todo: see if we need to support array
    // timePeriod?: any; // todo: add time period type and figure out how to integrate it downstream

    maxSelections?: number;
    allowMultiple?: boolean;
    busy?: boolean;
    setBusy?: (isBusy: boolean) => void;
}

export type PPGGroupInternalValue = Record<string, string>;

export type PPGGroupOptions = Record<string, string[]>;

export type SelectorType = {
    key: string;
    options: string[];
    ppgType: 'upload' | 'characteristic';
}

export type PpgGroupValidationStatus = 'neutral' | 'valid' | 'invalid';

export type DefaultPPGValueType = {brand: string[], size: string, pack: string};

export const ByzzerPPGGroupPicker = forwardRef<WithValue<boolean | undefined>, ByzzerPPGGroupPickerProps>(({
    onChange,
    name,
    label,
    className,
    disabledOptions = [],
    placeholder = 'Select from the list',
    value,
    disabled,
    onlyRenderIf,
    allowClear = true,
    showDescription = true,

    ppgId,

    categories,
    parentCompanies,
    manufacturers,
    brands,
    markets,
    // timePeriod,

    maxSelections = 1,
    allowMultiple = false,
    busy,
    setBusy,
    ...props
}, ref ) => {

    const baseClassName = 'byz-ppg-group-picker';

    const [internalValue, setInternalValue, internalValueRef] = useState<string | string[]>();
    const [allPpgGroups, setAllPpgGroups, allPpgGroupsRef] = useState<ByzzerSelectOption[]>([]);

    const { getPpgGroupNames } = useTenantApi();

    const fetchVersionRef = useRef(0);
    const fetchTimerRef = useRef<ReturnType<typeof setTimeout>>();

    useEffect(() => {
        if (value) {
            const newValue = maxSelections > 1 ? value : value?.[0];
            setInternalValue(newValue);
        }
    }, [value]);


    function sortByLabel(a, b) {
        return a?.display?.localeCompare(b?.display, 'en', {sensitivity: 'base'})
    }

    // Debounced fetch with stale response handling
    useEffect(() => {
        const currentVersion = ++fetchVersionRef.current;

        if (fetchTimerRef.current) {
            clearTimeout(fetchTimerRef.current);
        }

        if (!ppgId || !categories?.length) {
            setAllPpgGroups([]);
            setBusy?.(false);
            return;
        }

        setBusy?.(true);

        fetchTimerRef.current = setTimeout(async () => {
            try {
                let ppgInfoResponse: PpgGroupNames | undefined;

                ppgInfoResponse = await getPpgGroupNames({
                    ppgId,
                    categoryNames: categories,
                    ...(parentCompanies?.length ? { parentCompanyNames: parentCompanies } : {}),
                    ...(manufacturers?.length ? { manufacturerNames: manufacturers } : {}),
                    ...(brands?.length ? { brandNames: brands } : {}),
                });

                // Discard if deps changed while request was in-flight
                if (currentVersion !== fetchVersionRef.current) return;

                const allPpgGroupNames = ppgInfoResponse?.groups?.map((group) => {
                    return group.groupName;
                });
                setAllPpgGroups([...new Set(allPpgGroupNames ?? [])]
                    .map((name) => createPpgGroupNameOption(name))
                    .map(option => {
                        if (!showDescription) {
                            return {
                                display: option.display.substring(0, 12),
                                value: option.value.substring(0, 12)
                            };
                        }
                        return option;
                    })
                    .sort(sortByLabel)
                );

                if (!ppgInfoResponse) {
                    setBusy?.(false);
                    return;
                }
                setBusy?.(false);

            } catch (err) {
                if (currentVersion !== fetchVersionRef.current) return;
                setBusy?.(false);
            }
        }, 400);

        return () => {
            if (fetchTimerRef.current) {
                clearTimeout(fetchTimerRef.current);
            }
        };
    }, [ppgId, categories, brands, parentCompanies, manufacturers]);


    const handleValueSelect = (newValue: any) => {
        const value: string | undefined = (newValue as SingleValue<ByzzerSelectOption>)?.value;
        if (onChange) {
            onChange({
                value: value ? [value].flat() : [],
                name
            })
        }
        else {
            setInternalValue(value ? [value].flat() : []);
        }
    }

    function reset() {
        setAllPpgGroups([]);
        onChange?.({
            name,
            value: []
        })
    }

    return (
        <div className={classnames(`${baseClassName}`)}>
            <div className={`${baseClassName}__selectors`}>
                <ByzzerWindowedSelect
                    options={allPpgGroups}
                    value={internalValue}
                    onChange={handleValueSelect}
                    allowClear={true}
                    maxSelections={maxSelections}
                    allowMultiple={allowMultiple}
                    disabled={disabled || !allPpgGroups?.length}
                    name={name}
                    label={label}
                    placeholder={busy || !allPpgGroups?.length ? 'Loading...' : placeholder}
                />
            </div>
        </div>
    );

});

export default ByzzerPPGGroupPicker;

ByzzerPPGGroupPicker.displayName = 'ByzzerPPGGroupPicker';
